import { useEffect, useState, useRef } from "react";
import DOMPurify from "dompurify";
import { marked } from "marked";
import useScrollLockOnMount from "../Helpers/utils";

marked.setOptions({
  breaks: true
});

const AIElaborations = ({ post, onClose }) => {
  const [details, setDetails] = useState("");
  const [mode, setMode] = useState("scientific");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const buttonRefs = useRef({});
  const requestIdRef = useRef(0); // 👈 Track the most recent request

  const isMobile =
    /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  useScrollLockOnMount();

  const fetchElaboration = async (selectedMode) => {
    setLoading(true);
    setError(null);
    setDetails("");

    const currentRequestId = ++requestIdRef.current;

    // 🛑 KEY SECURITY: The API key and the complex query map 
    // are now handled securely on the PHP backend.

    // 1. Point to your PHP backend URL
    const apiUrl = "/php/elaborate.php"; // Adjust this path if your file is in a different directory

    const payload = {
      mode: selectedMode, // Send the selected mode
      full_form: post.full_form // Send the full form text
    };

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      // 2. Ignore outdated requests
      if (currentRequestId !== requestIdRef.current) return;

      // 3. Check for errors returned by the PHP script
      if (response.status !== 200 || result.error) {
        // Use the error message from the PHP backend if available
        throw new Error(result.error || "The server failed to process the request.");
      }

      // 4. Process the details returned from PHP
      const text = result.details;
      if (text) {
        const html = marked.parse(text);
        setDetails(html);
      } else {
        setError(
          "Failed to generate details. The AI may not have provided a valid response."
        );
      }
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      console.error("Error fetching elaboration:", err);
      setError(`Failed to fetch elaboration: ${err.message}`);
    } finally {
      if (currentRequestId === requestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (post && post.full_form) {
      fetchElaboration(mode);
    }
  }, [mode, post]);

  useEffect(() => {
    const selectedButton = buttonRefs.current[mode];
    if (selectedButton) {
      selectedButton.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest"
      });
    }
  }, [mode]);

  return (
    <div className="more-info-modal" style={{ display: "block" }}>
      <div id="more-info" style={{ display: "block" }}>
        {/* Header */}
        <header
          style={{
            backgroundColor: post.theme || "#333",
            padding: "10px",
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
            position: "sticky",
            top: 0,
            zIndex: 1
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <div
              style={{
                fontSize: "20px",
                marginLeft: "20px",
                color: post.text_bg || "white",
                textAlign: "center",
                width: "100%"
              }}
            >
              Explore AI Elaborations
            </div>
            <span
              style={{
                fontSize: "33px",
                color: post.text_bg,
                cursor: "pointer",
                marginTop: isMobile ? "-5px" : "1px"
              }}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              ×
            </span>
          </div>
        </header>

        {/* Mode buttons */}
        <div
          style={{
            position: "sticky",
            top: 0,
            display: "flex",
            overflowX: "auto",
            flexWrap: "nowrap",
            justifyContent: "flex-start",
            gap: "10px",
            margin: "10px",
            padding: "5px 0",
            scrollbarWidth: "none",
            "-ms-overflow-style": "none"
          }}
        >
          {[
            "scientific",
            "poetic",
            "whimsical",
            "practical",
            "historical",
            "analytical",
            "journalistic",
            "narrative",
            "humorous"
          ].map((modeName) => (
            <button
              ref={(el) => (buttonRefs.current[modeName] = el)}
              key={modeName}
              onClick={() => setMode(modeName)}
              style={{
                flexShrink: 0,
                padding: "8px 16px",
                borderRadius: "20px",
                border: "1px solid #ccc",
                backgroundColor: mode === modeName ? "#007bff" : "#f0f0f0",
                color: mode === modeName ? "white" : "#333",
                cursor: "pointer",
                textTransform: "capitalize"
              }}
            >
              {modeName}
            </button>
          ))}
        </div>

        {/* Main content */}
        <div
          style={{
            fontSize: "16px",
            textAlign: "center",
            color: "#333",
            margin: "7px",
            whiteSpace: "normal"
          }}
        >
          {loading ? (
            <div className="waiting-textcloud">
              <span>Thinking</span>
              <span>Generating</span>
              <span>Imagining</span>
              <span>Processing</span>
              <span>Loading</span>
              <span>Analyzing</span>
              <span>Preparing</span>
              <span>Understanding</span>
              <span>Creating</span>
              <span>Reasoning</span>
            </div>
          ) : error ? (
            <p style={{ color: "red" }}>Error: {error}</p>
          ) : (
            <div
              className="force-line-height"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(details || "Not Available")
              }}
              style={{ textAlign: "left" }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AIElaborations;
